/**
 * @deprecated This file is not used. Ticket details are fully shared in
 * @starjack/entities/ticket. Use useTicketDetailsBlocksFactory from
 * packages/entities/ticket/lib/hooks/useTicketDetails.tsx instead.
 */
import { ReactNode } from 'react';
import {
    getDurationString,
    getOrderStatusAsString,
    getTicketStatusColor,
    Identification,
    Ticket,
} from '@/models/ticket';
import { Box, Card, Chip } from '@mui/material';
import { TicketRefundBlock } from '@/components/TicketsOverviewUI/TicketDrawerContent/TicketRefundBlock';
import { TicketDrawerLabel } from '@/components/TicketsOverviewUI/TicketDrawerContent/TicketDrawerLabel';
import { TicketDrawerBlock } from '@/components/TicketsOverviewUI/TicketDrawerContent/TicketDrawerBlock';
import { getEnvironmentAsString } from '@/models/environment';
import { LiftOperator } from '@/models/liftoperator';
import { getMoneyAsString } from '@/utils';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { formatDateToString } from '@/components/TicketsOverviewUI/ticketUiUtils';
import { InsuranceCard } from '@/components/TicketsOverviewUI/TicketDrawerContent/InsuranceCard';

// util functions

export const ticketContentBlockIds = [
    'orderReference',
    'firstname',
    'lastname',
    'email',
    'ticketOwnerData',
    'identifications',
    'environment',
    'status',
    'cancelableCondition',
    'cancelable',
    'cancellationDate',
    'cancelledBy',
    'cancellationAction',
    'orderDisplayId',
    'orderDate',
    'startDate',
    'liftOperator',
    'duration',
    'ticketName',
    'personType',
    'price',
    'bookingOption',
    'priceReason',
    'pdfUrl',
    'insurance',
    'ticketId',
] as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const identificationContentBlockIds = [
    'keycard.serialNumber',
    'keycard.external',
    'keycardOrderedSameTime',
    'downloadLink',
    'birthdate',
    'firstname',
    'lastname',
] as const;

export type TicketContentBlockId = (typeof ticketContentBlockIds)[number];

export type IdentificationContentBlockId =
    (typeof identificationContentBlockIds)[number];

type TicketContentBlock = {
    blockId: TicketContentBlockId;
    header: string;
    content: ReactNode;
};

type IdentificationContentBlock = {
    blockId: IdentificationContentBlockId;
    header: string;
    content: ReactNode;
};

export const getAllTicketDetailsBlocks = ({
    ticket,
    identifications,
    liftOperator,
}: {
    ticket: Ticket;
    identifications: IdentificationContentBlock[][];
    liftOperator: LiftOperator;
}): TicketContentBlock[] => [
    {
        blockId: 'orderReference',
        header: 'Referenznummer',
        content: (
            <TicketDrawerLabel>
                {ticket.orderReference || '-'}
            </TicketDrawerLabel>
        ),
    },
    {
        blockId: 'firstname',
        header: 'Vorname',
        content: <TicketDrawerLabel>{ticket.firstname}</TicketDrawerLabel>,
    },
    {
        blockId: 'lastname',
        header: 'Nachname',
        content: <TicketDrawerLabel>{ticket.lastname}</TicketDrawerLabel>,
    },
    {
        blockId: 'email',
        header: 'E-Mail Buchender',
        content: <TicketDrawerLabel>{ticket.email}</TicketDrawerLabel>,
    },
    {
        blockId: 'ticketOwnerData',
        header: 'Ticketkäufer (+ Buchungsnummer)',
        content: (
            <TicketDrawerLabel>
                {ticket.lastname} {ticket.firstname}{' '}
                {ticket.permissionNumber && `, (${ticket.permissionNumber})`}{' '}
                {ticket.barcodeNumber && `, (${ticket.barcodeNumber})`}
            </TicketDrawerLabel>
        ),
    },
    {
        blockId: 'orderDisplayId',
        header: 'Starjack Bestellnummer',
        content: <TicketDrawerLabel>{ticket.orderDisplayId}</TicketDrawerLabel>,
    },

    {
        blockId: 'orderDate',
        header: 'Bestellzeitpunkt',
        content: (
            <TicketDrawerLabel>
                {formatDateToString(ticket.orderDate, { detailed: true })}
            </TicketDrawerLabel>
        ),
    },

    {
        blockId: 'startDate',
        header: 'Starttag',
        content: (
            <TicketDrawerLabel>
                {formatDateToString(ticket.startDate)}
            </TicketDrawerLabel>
        ),
    },

    {
        blockId: 'liftOperator',
        header: 'Bergbahn',
        content: (
            <TicketDrawerLabel>
                {liftOperator?.name?.de || '-'}
            </TicketDrawerLabel>
        ),
    },

    {
        blockId: 'duration',
        header: 'Dauer',
        content: (
            <TicketDrawerLabel>
                {getDurationString(ticket.duration, ticket.type)}
            </TicketDrawerLabel>
        ),
    },

    {
        blockId: 'ticketName',
        header: 'Ticketname',
        content: <TicketDrawerLabel>{ticket.name}</TicketDrawerLabel>,
    },

    {
        blockId: 'personType',
        header: 'Personentyp',
        content: <TicketDrawerLabel>{ticket.personName}</TicketDrawerLabel>,
    },

    {
        blockId: 'price',
        header: 'Preis',
        content: (
            <TicketDrawerLabel>
                {getMoneyAsString(ticket.price)}
            </TicketDrawerLabel>
        ),
    },

    {
        blockId: 'bookingOption',
        header: 'Buchungsoption',
        content: (
            <TicketDrawerLabel>{ticket.identificationType}</TicketDrawerLabel>
        ),
    },

    {
        blockId: 'priceReason',
        header: 'Preisgrund',
        content: (
            <TicketDrawerLabel>{ticket.priceReason || '-'}</TicketDrawerLabel>
        ),
    },

    {
        blockId: 'ticketId',
        header: 'Ticket ID',
        content: <TicketDrawerLabel>{ticket.id || '-'}</TicketDrawerLabel>,
    },

    {
        blockId: 'identifications',
        header: 'Personen',
        content: identifications.map((identificationBlocks, index) => (
            <Card key={index}>
                {identificationBlocks.map((identificationBlock) => (
                    <TicketDrawerBlock
                        header={identificationBlock.header}
                        key={identificationBlock.blockId}
                    >
                        {identificationBlock.content}
                    </TicketDrawerBlock>
                ))}
            </Card>
        )),
    },

    {
        blockId: 'insurance',
        header: 'Versicherung',
        content: ticket.insurance ? (
            <InsuranceCard ticket={ticket} />
        ) : (
            <TicketDrawerLabel>Keine Versicherung gebucht</TicketDrawerLabel>
        ),
    },

    {
        blockId: 'environment',
        header: 'Verkaufsoberfläche',
        content: (
            <Chip
                color='primary'
                label={
                    <TicketDrawerLabel>
                        {getEnvironmentAsString(ticket.environment)}
                    </TicketDrawerLabel>
                }
            />
        ),
    },

    {
        blockId: 'pdfUrl',
        header: 'PDF Download Link',
        content: (
            <>
                {ticket.pdfUrl ? (
                    <Chip
                        color='primary'
                        onClick={() => {
                            if (ticket.pdfUrl) {
                                window.open(ticket.pdfUrl, '_blank');
                            }
                        }}
                        label={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <DownloadForOfflineIcon fontSize='small' />
                                <TicketDrawerLabel
                                    sx={{
                                        ml: 2,
                                    }}
                                >
                                    PDF Download
                                </TicketDrawerLabel>
                            </Box>
                        }
                    />
                ) : (
                    <TicketDrawerLabel>Kein PDF Verfügbar</TicketDrawerLabel>
                )}
            </>
        ),
    },
    {
        blockId: 'status',
        header: 'Status',
        content: (
            <Chip
                color={getTicketStatusColor(ticket.status)}
                label={
                    <TicketDrawerLabel>
                        {getOrderStatusAsString(ticket.status)}
                    </TicketDrawerLabel>
                }
            />
        ),
    },
    {
        blockId: 'cancelableCondition',
        header: 'Regel Stornierbar',
        content: (
            <Chip
                label={
                    <TicketDrawerLabel>
                        {ticket.cancelableCondition
                            ? 'Regel stornierbar'
                            : 'Regel nicht stornierbar'}
                    </TicketDrawerLabel>
                }
                color={ticket.cancelableCondition ? 'success' : 'error'}
            />
        ),
    },
    {
        blockId: 'cancelable',
        header: 'Ticket Stornierbar',
        content: (
            <Chip
                label={
                    <TicketDrawerLabel>
                        {ticket.cancelable
                            ? 'Ticket stornierbar'
                            : 'Ticket nicht stornierbar'}
                    </TicketDrawerLabel>
                }
                color={ticket.cancelable ? 'success' : 'error'}
            />
        ),
    },
    {
        blockId: 'cancellationDate',
        header: 'Refundieren / Stornieren',
        content: <TicketRefundBlock ticket={ticket} />,
    },
];

export const getAllIdentificationBlocks = (
    identifications: Identification
): IdentificationContentBlock[] => [
    {
        blockId: 'firstname',
        header: 'Vorname',
        content: (
            <TicketDrawerLabel>
                {identifications.firstname || '-'}
            </TicketDrawerLabel>
        ),
    },

    {
        blockId: 'lastname',
        header: 'Nachname',
        content: (
            <TicketDrawerLabel>
                {identifications.lastname || '-'}
            </TicketDrawerLabel>
        ),
    },

    {
        blockId: 'keycard.serialNumber',
        header: 'Keycardseriennummer',
        content: (
            <TicketDrawerLabel>
                {identifications.keycard?.serialNumber || '-'}
            </TicketDrawerLabel>
        ),
    },
    {
        blockId: 'keycard.external',
        header: 'Neue Keycard/Registrierte Keycard oder Wiederaufladung',
        content: (
            <TicketDrawerLabel>
                {identifications.keycard?.external || '-'}
            </TicketDrawerLabel>
        ),
    },
    {
        blockId: 'keycardOrderedSameTime',
        header: 'Zeitpunkt Info Produktion',
        content: (
            <TicketDrawerLabel>
                {identifications.keycardOrderedSameTime
                    ? formatDateToString(identifications.productionDate)
                    : '-'}
            </TicketDrawerLabel>
        ),
    },
    {
        blockId: 'birthdate',
        header: 'Geburtsdatum',
        content: (
            <TicketDrawerLabel>
                {formatDateToString(identifications.birthday)}
            </TicketDrawerLabel>
        ),
    },
];
